/**
 * Payment Result Modal — shows payment status as bottom sheet overlay.
 * Polls processing payments, shows final result with haptic feedback.
 * Auto-closes after 5s on completed/failed.
 */

import { useEffect, useRef, useState, useCallback, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { CheckCircle2, XCircle, Loader2, Copy, Check } from 'lucide-react'
import { useAuthStore } from '@turnifly/shared/stores'
import { usePaymentStatus, paymentKeys } from '@turnifly/shared/hooks'
import { triggerHapticNotification as hapticNotification } from '@turnifly/shared/utils'
import { Button } from '@turnifly/shared/ui'
import type { PendingPayment } from '@turnifly/shared/hooks'

interface PaymentResultModalProps {
  payment: PendingPayment | null
  isOpen: boolean
  onClose: () => void
  onUpdate: (p: PendingPayment) => void
}

export const PaymentResultModal = memo(function PaymentResultModal({
  payment, isOpen, onClose, onUpdate,
}: PaymentResultModalProps): React.ReactElement | null {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const userId = useAuthStore((s) => s.user?.id)
  const hapticDone = useRef(false)
  const autoCloseRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  const isProcessing = payment?.status === 'processing'
  const { data: polled } = usePaymentStatus(
    isProcessing ? payment?.id : undefined,
    isProcessing,
  )

  // Update payment when polling returns new status
  useEffect(() => {
    if (!polled || !payment || !isProcessing) return
    if (polled.status !== 'processing' && polled.status !== 'pending') {
      onUpdate({ ...payment, status: polled.status })
    }
  }, [polled, payment, isProcessing, onUpdate])

  const status = payment?.status ?? 'processing'
  const isCompleted = status === 'completed'
  const isFailed = status === 'failed' || status === 'cancelled'
  const isFinal = isCompleted || isFailed

  // Haptic feedback on final state
  useEffect(() => {
    if (!isFinal || hapticDone.current) return
    hapticDone.current = true
    hapticNotification(isCompleted ? 'success' : 'error')

    if (isCompleted && userId) {
      void queryClient.invalidateQueries({ queryKey: paymentKeys.subscription(userId) })
      void queryClient.invalidateQueries({ queryKey: ['payment', 'history'] })
    }
  }, [isFinal, isCompleted, userId, queryClient])

  // Auto-close after 5s on final state
  useEffect(() => {
    if (!isFinal) return
    autoCloseRef.current = setTimeout(onClose, 5000)
    return () => clearTimeout(autoCloseRef.current)
  }, [isFinal, onClose])

  // Reset haptic ref when modal opens
  useEffect(() => {
    if (isOpen) hapticDone.current = false
  }, [isOpen])

  if (!isOpen || !payment) return null

  const meta = payment.metadata ?? {}
  const paymentType = payment.type
  const promoCode = meta.promo_code as string | undefined

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" onClick={onClose} />
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
        className="fixed bottom-0 left-0 right-0 z-[51] bg-[var(--color-bg)] rounded-t-[28px] max-w-lg mx-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-center pt-3">
          <div className="w-10 h-1 bg-white/[0.12] rounded-full" />
        </div>

        <div className="px-6 pt-6 pb-8 flex flex-col items-center text-center pb-safe">
          {/* Processing */}
          {!isFinal && (
            <>
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-5">
                <Loader2 size={40} className="text-primary animate-spin" />
              </div>
              <h3 className="text-[18px] font-display font-bold text-text mb-2">
                {t('payment.modal.checking')}
              </h3>
              <p className="text-[13px] text-text-hint/50 leading-relaxed max-w-[260px]">
                {t('payment.modal.pleaseWait')}
              </p>
            </>
          )}

          {/* Completed */}
          {isCompleted && (
            <>
              <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center mb-5">
                <CheckCircle2 size={40} className="text-emerald-400" />
              </div>
              <h3 className="text-[18px] font-display font-bold text-text mb-2">
                {t('payment.modal.success')}
              </h3>
              <p className="text-[13px] text-text-hint/50 mb-4">
                {paymentType === 'premium' && t('payment.modal.premiumActivated')}
                {paymentType === 'challenge' && t('payment.modal.challengeJoined')}
                {paymentType === 'gift' && t('payment.modal.giftReady')}
              </p>

              {/* Gift promo code */}
              {paymentType === 'gift' && promoCode && (
                <PromoCodeDisplay code={promoCode} />
              )}
            </>
          )}

          {/* Failed */}
          {isFailed && (
            <>
              <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center mb-5">
                <XCircle size={40} className="text-red-400" />
              </div>
              <h3 className="text-[18px] font-display font-bold text-text mb-2">
                {t('payment.modal.failed')}
              </h3>
              <p className="text-[13px] text-text-hint/50 mb-4 max-w-[260px]">
                {t('payment.modal.failedDesc')}
              </p>
            </>
          )}

          {/* Action button */}
          <div className="w-full mt-4">
            <Button variant={isFailed ? 'primary' : 'glass'} fullWidth onClick={onClose}>
              {isFailed ? t('payment.modal.retry') : t('payment.modal.close')}
            </Button>
          </div>
        </div>
      </motion.div>
    </>
  )
})

// ── Promo Code Display ──

function PromoCodeDisplay({ code }: { code: string }): React.ReactElement {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback((): void => {
    void navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [code])

  return (
    <div className="w-full bg-white/[0.04] border border-white/[0.06] rounded-xl p-4 mb-2">
      <p className="text-[11px] text-text-hint/40 mb-1.5">{t('payment.modal.giftCodeLabel')}</p>
      <div className="flex items-center gap-2">
        <code className="flex-1 text-[18px] font-mono font-bold text-text tracking-wider">
          {code}
        </code>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary/10 text-primary text-[12px] font-medium tap-highlight-transparent"
        >
          {copied ? <Check size={13} /> : <Copy size={13} />}
          {copied ? t('payment.modal.codeCopied') : t('payment.modal.copyCode')}
        </button>
      </div>
    </div>
  )
}
